import type { SerializeFrom } from '~/types/serialize-from';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useFetcher } from 'react-router';
import { atom, useAtom } from 'jotai';
import { useActivitySensor } from '@archipro-website/react-hooks';
import {
    CONVERSATIONS_API,
    type loader,
} from '~/routes/remix-api.chat.conversations';
import { useUserLoggedIn } from '~/modules/user';
import { useNotifications } from '~/modules/notifications';
import type { Conversation } from '../types';
import type { ConversationStatusFilter } from '../utils/conversation-status';
import { useConversationCache } from './use-conversation-cache';
import { useRealTimeChat } from './use-real-time-chat';
import { useClearTypingIndicator } from './use-typing-indicator';

type ConversationsResult = NonNullable<SerializeFrom<typeof loader>>;
type PageInfo = ConversationsResult['pageInfo'];

/**
 * The selected inbox: personal when `null`, otherwise a company professional ID.
 */
export const inboxProfessionalIDAtom = atom<number | null>(null);

/**
 * Filter parameters sent to the `getConversations` query.
 */
export const conversationsQueryParamsAtom = atom<{
    search: string;
    buildHubIDs: string[];
    statusFilter: ConversationStatusFilter;
    branchProfessionalID: number | null;
}>({
    search: '',
    buildHubIDs: [],
    statusFilter: 'all',
    branchProfessionalID: null,
});

/**
 * The shared conversations state.
 */
const conversationsStateAtom = atom<{
    professionalID: number | null;
    conversations: Conversation[];
    pageInfo: PageInfo | null;
    offset: number;
} | null>(null);

/**
 * Hook to get a paginated list of conversations for the current user.
 */
export function useConversations({
    global = false,
    isDisabled = false,
}: {
    /**
     * Run fetch, pagination, polling, and cache-sync effects. Only one instance
     * should set this to `true` (in the root component) to avoid
     * duplicate requests.
     */
    global?: boolean;
    isDisabled?: boolean;
} = {}) {
    const { lastCachedConversation } = useConversationCache();
    const pageActive = useActivitySensor();
    const isFetched = useRef(false);
    const processedDataRef = useRef<ConversationsResult | null>(null);
    const { isLoggedIn } = useUserLoggedIn();
    const [professionalID, setProfessionalID] = useAtom(
        inboxProfessionalIDAtom,
    );
    const [queryParams, setQueryParams] = useAtom(conversationsQueryParamsAtom);
    const { search, buildHubIDs, statusFilter, branchProfessionalID } =
        queryParams;
    const [conversationsState, setConversationsState] = useAtom(
        conversationsStateAtom,
    );
    const fetcher = useFetcher<typeof loader>({ key: 'useConversations' });
    const currentConversationsState = useMemo(() => {
        if (!conversationsState) return null;
        return conversationsState.professionalID === professionalID
            ? conversationsState
            : null;
    }, [conversationsState, professionalID]);
    const conversations = currentConversationsState?.conversations ?? null;
    const pageInfo = currentConversationsState?.pageInfo ?? null;
    const offset = currentConversationsState?.offset ?? 0;

    const buildApiUrl = useCallback(
        (offset: number) => {
            const url = new URL(CONVERSATIONS_API, 'https://archipro.co.nz');
            if (professionalID && professionalID > 0) {
                url.searchParams.set('professionalID', String(professionalID));
            }
            if (professionalID && professionalID > 0 && branchProfessionalID) {
                url.searchParams.set(
                    'branchProfessionalID',
                    String(branchProfessionalID),
                );
            }
            url.searchParams.set('q', search);
            url.searchParams.set('statusFilter', statusFilter);
            url.searchParams.set('limit', '20');
            url.searchParams.set('offset', String(offset));
            for (const buildHubID of buildHubIDs) {
                url.searchParams.append('buildHubIDs', buildHubID);
            }
            return `${url.pathname}${url.search}`;
        },
        [
            professionalID,
            branchProfessionalID,
            search,
            statusFilter,
            buildHubIDs,
        ],
    );

    const apiUrl = useMemo(() => {
        return buildApiUrl(offset);
    }, [offset, buildApiUrl]);

    const refetch = useCallback(() => {
        setConversationsState((prev) => (prev ? { ...prev, offset: 0 } : prev));
        fetcher.load(buildApiUrl(0));
    }, [fetcher, buildApiUrl, setConversationsState]);

    const { isConnected } = useConversationsRealTime({
        refetch,
        global,
        isDisabled,
    });

    const setInbox = useCallback(
        (nextProfessionalID: number | null) => {
            if (professionalID === nextProfessionalID) return;
            setProfessionalID(nextProfessionalID);
            setQueryParams({
                search: '',
                buildHubIDs: [],
                statusFilter: 'all',
                branchProfessionalID: null,
            });
            setConversationsState(null);
        },
        [
            professionalID,
            setProfessionalID,
            setQueryParams,
            setConversationsState,
        ],
    );

    const setBranch = useCallback(
        (nextBranchProfessionalID: number | null) => {
            if (branchProfessionalID === nextBranchProfessionalID) return;
            setQueryParams((prev) => ({
                ...prev,
                branchProfessionalID: nextBranchProfessionalID,
            }));
            setConversationsState(null);
        },
        [branchProfessionalID, setQueryParams, setConversationsState],
    );

    const setParams = useCallback(
        (params: {
            search?: string;
            buildHubIDs?: string[];
            statusFilter?: ConversationStatusFilter;
        }) => {
            setQueryParams((prev) => ({
                ...prev,
                ...(params.search !== undefined
                    ? { search: params.search.trim() }
                    : {}),
                ...(params.buildHubIDs !== undefined
                    ? { buildHubIDs: params.buildHubIDs }
                    : {}),
                ...(params.statusFilter !== undefined
                    ? { statusFilter: params.statusFilter }
                    : {}),
            }));
            setConversationsState((prev) =>
                prev
                    ? {
                          ...prev,
                          pageInfo: prev.pageInfo
                              ? // Prevent loading next page and resetting the offset.
                                { ...prev.pageInfo, hasNextPage: false }
                              : prev.pageInfo,
                          offset: 0,
                      }
                    : prev,
            );
            // Wait for the fetcher to update the conversations.
        },
        [setQueryParams, setConversationsState],
    );

    const loadMore = useCallback(() => {
        if (
            fetcher.state !== 'idle' ||
            !conversations ||
            !pageInfo?.hasNextPage
        ) {
            return;
        }
        setConversationsState((prev) => {
            if (prev && prev.offset < prev.conversations.length) {
                return { ...prev, offset: prev.conversations.length };
            }
            return prev;
        });
    }, [fetcher.state, conversations, pageInfo, setConversationsState]);

    const removeConversation = useCallback(
        (conversationID: number, inboxProfessionalID: number | null) => {
            setConversationsState((prev) => {
                if (!prev || prev.professionalID !== inboxProfessionalID) {
                    return prev;
                }
                if (
                    prev.conversations.some(
                        (c) => c.ID === String(conversationID),
                    )
                ) {
                    return {
                        ...prev,
                        conversations: prev.conversations.filter(
                            (c) => c.ID !== String(conversationID),
                        ),
                    };
                }
                return prev;
            });
        },
        [setConversationsState],
    );

    // Fetch conversations on mount and when the query params change.
    useEffect(() => {
        if (!global || !isLoggedIn || isDisabled) return;
        if (fetcher.state === 'idle' && !isFetched.current) {
            fetcher.load(apiUrl);
            isFetched.current = true;
        } else if (isFetched.current) {
            // Refetch when the query params change.
            fetcher.load(apiUrl);
        }
        // Must not include `fetcher` to avoid infinite loop.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [global, isLoggedIn, isDisabled, apiUrl]);

    // Set data from the fetcher.
    useEffect(() => {
        if (!global || !isLoggedIn || isDisabled) return;
        if (
            !fetcher.data ||
            fetcher.state !== 'idle' ||
            fetcher.data === processedDataRef.current
        ) {
            return;
        }
        const newConversations = fetcher.data.conversations;
        const newPageInfo = fetcher.data.pageInfo;
        setConversationsState((prev) => {
            if (
                !prev ||
                prev.professionalID !== professionalID ||
                !newPageInfo.hasPreviousPage
            ) {
                return {
                    professionalID,
                    conversations: newConversations,
                    pageInfo: newPageInfo,
                    offset: 0,
                };
            }
            const next = [...prev.conversations];
            for (const conversation of newConversations) {
                if (!prev.conversations.some((c) => c.ID === conversation.ID)) {
                    next.push(conversation);
                }
            }
            return {
                professionalID,
                conversations: next,
                pageInfo: newPageInfo,
                offset,
            };
        });
        processedDataRef.current = fetcher.data;
    }, [
        global,
        isLoggedIn,
        isDisabled,
        fetcher,
        professionalID,
        offset,
        setConversationsState,
    ]);

    // Update the chat settings and unread message count as soon as possible
    // from the conversation cache.
    useEffect(() => {
        if (!global || !isLoggedIn || isDisabled) return;
        if (
            !lastCachedConversation ||
            lastCachedConversation.professionalID !== professionalID
        ) {
            return;
        }
        const cachedConversation = lastCachedConversation.conversation;
        setConversationsState((prev) => {
            if (
                prev?.conversations.some(
                    (c) => c.ID === String(cachedConversation.ID),
                )
            ) {
                const next = prev.conversations.map((c) =>
                    c.ID === String(cachedConversation.ID)
                        ? {
                              ...c,
                              UnreadMessageCount:
                                  cachedConversation.UnreadMessageCount,
                              IsImportant: cachedConversation.IsImportant,
                              IsArchived: cachedConversation.IsArchived,
                              IsDone: cachedConversation.IsDone,
                              IsMuted: cachedConversation.IsMuted,
                              ...(cachedConversation.__typename ===
                              'DirectConversation'
                                  ? {
                                        Name: cachedConversation.Name,
                                        BuildHub: cachedConversation.BuildHub,
                                        Thumbnail: cachedConversation.Thumbnail,
                                        IsArchived:
                                            cachedConversation.IsArchived,
                                    }
                                  : {}),
                          }
                        : c,
                );
                return { ...prev, conversations: next };
            }
            return prev;
        });
    }, [
        global,
        isLoggedIn,
        isDisabled,
        lastCachedConversation,
        professionalID,
        setConversationsState,
    ]);

    // Poll every 5 minutes if the websocket is connected or the page is
    // inactive; fall back to 30 seconds when the websocket is disconnected
    // and the user is active.
    useEffect(() => {
        if (!global || !isLoggedIn || isDisabled) return;
        const pollingIntervalMs = isConnected || !pageActive ? 300_000 : 30_000;
        const interval = setInterval(() => {
            refetch();
        }, pollingIntervalMs);
        return () => clearInterval(interval);
    }, [global, isLoggedIn, isDisabled, isConnected, pageActive, refetch]);

    return {
        /**
         * The list of conversations.
         */
        conversations,
        /**
         * There are more conversations to load.
         */
        hasMore: pageInfo?.hasNextPage ?? false,
        /**
         * The conversations are currently being loaded.
         */
        isLoading: fetcher.state !== 'idle',
        /**
         * The selected inbox professional ID, or `null` for the personal inbox.
         */
        professionalID,
        /**
         * The search query to filter conversations.
         */
        search,
        /**
         * The BuildHub project IDs to filter conversations.
         */
        buildHubIDs,
        /**
         * The active inbox status filter.
         */
        statusFilter,
        /**
         * The selected branch professional ID, or `null` for all linked profiles.
         */
        branchProfessionalID,
        /**
         * Switch between the personal inbox and a company inbox.
         */
        setInbox,
        /**
         * Set the branch filter for the company inbox.
         */
        setBranch,
        /**
         * Set the filter parameters for the `GetConversations` query. Parameters
         * that are `undefined` will not be changed.
         */
        setParams,
        /**
         * Load more conversations by fetching the next page. This increments the
         * offset and appends the new results to the existing conversations.
         */
        loadMore,
        /**
         * Remove a conversation from the list of conversations.
         */
        removeConversation,
    };
}

function useConversationsRealTime({
    global,
    isDisabled,
    refetch,
}: {
    global: boolean;
    isDisabled: boolean;
    refetch: () => void;
}) {
    const { user, isLoggedIn } = useUserLoggedIn();
    const clearTypingIndicator = useClearTypingIndicator();
    const { invalidateCachedConversation } = useConversationCache();
    const { refetch: refetchNotifications } = useNotifications();

    return useRealTimeChat({
        onMessage: (message) => {
            if (!global || !isLoggedIn || isDisabled) return;
            if (
                message.type !== 'chat/message' &&
                message.type !== 'chat/migrate' &&
                message.type !== 'chat/update' &&
                message.type !== 'chat/read' &&
                message.type !== 'chat/status-update' &&
                message.type !== 'chat/removed' &&
                message.type !== 'chat/message/updated'
            ) {
                return;
            }
            refetch();
            refetchNotifications();
            const inboxContexts = [
                null,
                ...user.Professionals.filter((p) => p.HasInboxAccess).map(
                    (p) => p.ID,
                ),
            ];
            // Invalidate cached conversation avoid showing outdated messages.
            for (const professionalID of inboxContexts) {
                invalidateCachedConversation(
                    message.conversation_id,
                    professionalID,
                );
            }
            if (message.type === 'chat/migrate') {
                for (const professionalID of inboxContexts) {
                    invalidateCachedConversation(
                        message.original_conversation_id,
                        professionalID,
                    );
                }
            }
            if (
                message.type === 'chat/message' ||
                message.type === 'chat/migrate'
            ) {
                // Clear the typing indicator for a participant after they send a message.
                clearTypingIndicator(
                    message.conversation_id,
                    message.participant_id,
                );
            }
        },
    });
}
